import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ChangeDetectionStrategy,
  ComponentRef,
  OnInit,
} from '@angular/core';
import { Observable, switchMap, of, finalize, shareReplay, map } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute } from '@angular/router';

import { DprWeatherGridSync } from '@dpr-details/models/sync';
import { DprDetailsService } from '@dpr-details/services';
import { getDefaultSubMenuItems } from '@dpr-details/utils';
import { DprDetailsSync, DprWeather } from '@shared/models/dpr';
import { DprWeatherDetailGridData } from '@dpr-details/models/grid';
import { GridSync } from '@grid/models';
import { DprWeatherRegistrationsGridComponent } from '@dpr-details/components';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { DprDetailsPageBaseComponent } from '@dpr-details/models/component';
import { DprWeatherGridService } from '@dpr-details/services/grid';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';
import { StaticMessage } from '@shared/enums';
import { NavigationMenuItem } from '@shared/models';
import { ScreenSizeService } from '@shared/services';

@UntilDestroy()
@Component({
  selector: 'app-dpr-weather-page',
  templateUrl: './dpr-weather-page.component.html',
  styleUrls: ['./dpr-weather-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprWeatherPageComponent
  extends DprDetailsPageBaseComponent<DprWeatherDetailGridData>
  implements OnInit
{
  labeling = {
    gridEntityName: $localize`:@@dprDetails.dprWeatherPage.txtWeatherRegistrations:Weather Registrations`,
    timeFieldName: $localize`:@@dprDetails.dprWeatherPage.colTime:Time`,
  };

  StaticMessage = StaticMessage;

  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = getDefaultSubMenuItems();

  constructor(
    private dprDetailsService: DprDetailsService,
    protected activatedRoute: ActivatedRoute,
    protected dprWeatherGridService: DprWeatherGridService,
    protected dprAttachmentService: DprAttachmentService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute, dprAttachmentService);
  }

  private get dprWeather(): DprWeather {
    return this.dprDetails.dprWeather;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.preselectedItem =
      this.activatedRoute?.firstChild?.snapshot?.url[0]?.path;
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    // store activated component changes
    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(
            this.registrationsChanges,
            this.attachmentsChanges
          );

    const saveChanges$ = validateChanges$.pipe(
      untilDestroyed(this),
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(
          this.registrationsChanges,
          this.attachmentsChanges,
          this.comment,
          isAutoSyncFromAdminChange
        );
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  protected isRegistrationComponent(component: ComponentRef<any>): boolean {
    return component instanceof DprWeatherRegistrationsGridComponent;
  }

  protected setupData(): void {
    this.registrationsGridData = this.dprWeatherGridService.buildGridData(
      this.dprWeather?.dprWeatherDetails
    );
    this.attachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprWeather?.dprWeatherAttachments
    );
    this.comment = this.dprWeather.comment;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprWeatherDetailGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprWeather?.syncDprWeatherDetails;
    if (!offlineChanges) {
      return null;
    }

    const offlineGridData = this.dprWeatherGridService.buildGridData([
      ...(offlineChanges.addedList ?? []),
      ...(offlineChanges.updatedList ?? []),
    ]);

    return this.getOfflineGridDataChanges(offlineGridData, offlineChanges);
  }

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprAttachmentsGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprWeather?.syncDprWeatherAttachments;
    return this.getOfflineAttachmentGridDataChanges(offlineChanges);
  }

  //#region Private methods

  private validateChanges(
    registrationsChanges: GridSync<DprWeatherDetailGridData>,
    attachmentsChanges: GridSync<DprAttachmentsGridData>
  ): Observable<boolean> {
    if (
      !this.dprWeatherGridService.validateChanges(
        registrationsChanges,
        this.labeling
      )
    ) {
      return of(false);
    }

    if (!this.dprAttachmentService.validateChanges(attachmentsChanges)) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    registrationsChanges: GridSync<DprWeatherDetailGridData>,
    attachmentsChanges: GridSync<DprAttachmentsGridData>,
    comment: string,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprWeather> {
    const syncDprWeatherDetails = this.dprWeatherGridService.mapToGridSync(
      this.dprWeather.id,
      registrationsChanges,
      this.dprWeather.dprWeatherDetails,
      this.selectedSubproject.data.weatherList
    );

    const syncData: DprWeatherGridSync =
      this.dprDetailsSyncModelBuilderService.forDprWeather(
        this.selectedSubproject.id,
        this.dprDetails.id,
        this.dprWeather.id,
        syncDprWeatherDetails,
        attachmentsChanges,
        comment
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprWeather(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.weatherTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprWeather): void {
    this.refreshData();
    this.offlineFacade.setDprWeather(result, this.dprDetails.id);
  }

  //#endregion
}
